import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { useSnackbar } from 'notistack';
import { Paper, Grid } from '@material-ui/core';
import { QmeTrackerTabs, QmeTrackerList, QmeTrackerCRU } from '../components';
import { TableListComp, ExportManu, EmptyCollection, PageLoader } from '../../../shared_elements';
import FilterComponent from '../../../shared_elements/filter_component';
import { STabsLoader, STableLoader, SHeaderLoader } from '../../../shared_elements/loaders';
import { sLesseeListAc } from '../../../shared/actionCreators';
import { checkApiStatus, onCheckPermission, removeEmptyKey } from '../../../utils_v2';
import { trackActivity } from '../../../utils/mixpanel';
import DeploymentMessage from '../../../shared/components/DeploymentMessage';
import LogoutInfo from '../../../shared/components/LogoutInfo';
import { getQmeTrackerApi, getQmeTrackerUsersApi, exportQmeTrackerApi } from '../apiServices';
import { qmeTrackerHd, qmeFilterOps } from '../';

const QmeTracker = ({ location, lessee, getLesseeList }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [skeletonLoader, setSkeletonLoader] = useState(true);
  const [pageLoader, setPageLoader] = useState(false);
  const [filter, setFilter] = useState({});
  const [sort, setSort] = useState({ sort: location.query.sort || '', sort_by: location.query.sort_by || '' });
  const [qmeTrackerInfo, setQmeTrackerInfo] = useState({ list: [], pagination: {}, summary: {} });
  const [qmeTracker, setQmeTracker] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    getQmeTracker({ per_page: 10, ...location.query }, 'skeletonLoader');
    getLesseeList();
    getQmeTrackerUsersApi().then(response => {
      if (checkApiStatus(response)) {
        setUsers(response.data.data);
      }
    });
    trackActivity('Project Management', {
      event_type: 'Page Visited',
      page_title: 'QME Tracker',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getQmeTracker = (query = {}, loaderType) => {
    loaderType === 'skeletonLoader' ? setSkeletonLoader(true) : setPageLoader(true);
    query = removeEmptyKey({ ...query });
    browserHistory.push({ ...browserHistory.getCurrentLocation(), query });
    getQmeTrackerApi(query)
      .then(response => {
        loaderType === 'skeletonLoader' ? setSkeletonLoader(false) : setPageLoader(false);
        if (checkApiStatus(response)) {
          const { sort, sort_by, page, per_page, ...appliedFilter } = query;
          setQmeTrackerInfo(response.data.data);
          setFilter(appliedFilter);
        } else {
          enqueueSnackbar(response?.data?.message || 'Unable to fetch QME Tracker', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
        }
      });
  };
  const createSortHandler = (sortField) => {
    const sort_by = sortField === sort.sort && sort.sort_by === 'asc' ? 'desc' : 'asc';
    setSort({ sort: sortField, sort_by });
    getQmeTracker({ ...filter, sort: sortField, sort_by, per_page: qmeTrackerInfo.pagination.per_page }, 'pageLoader');
  };
  const handleTabChange = (event, newValue) => {
    getQmeTracker({ ...filter, status: newValue === 'all' ? '' : newValue, ...sort, per_page: qmeTrackerInfo.pagination.per_page }, 'pageLoader');
  };
  const exportQmeTracker = (file) => {
    enqueueSnackbar('Downloading QME Tracker ...', { autoHideDuration: 30000, variant: 'info', anchorOrigin: { vertical: 'bottom', horizontal: 'right' } });
    exportQmeTrackerApi({ ...filter, ...sort }, file)
      .then(() => {
        closeSnackbar();
        enqueueSnackbar('QME Tracker Downloaded', { autoHideDuration: 2000, variant: 'success', anchorOrigin: { vertical: 'bottom', horizontal: 'right' } });
      });
  };
  const tabIndex = !filter.status ? 'all' : filter.status.toString().includes(',') ? false : filter.status.toString();
  return (
    <section className='projects-management-section'>
      <DeploymentMessage />
      <LogoutInfo />
      <div className="projects-list-sec">
        {skeletonLoader ? <STabsLoader count={5} /> :
          <QmeTrackerTabs tabIndex={tabIndex} count={qmeTrackerInfo.summary || {}} handleTabChange={handleTabChange} />
        }
        {skeletonLoader ?
          <SHeaderLoader buttons={onCheckPermission('project_management', 'projects', 'EXP') ? [88] : []} /> :
          <Paper square className="filter-cta">
            <Grid alignItems='center' container spacing={1}>
              <Grid item xs={9}>
                <FilterComponent
                  filter={filter}
                  filterMenu={{ ...qmeFilterOps, lessee: { ...qmeFilterOps.lessee, options: lessee } }}
                  getResponseBack={(applyFilter) => getQmeTracker({ ...applyFilter, ...sort, page: 1, per_page: qmeTrackerInfo.pagination.per_page }, 'pageLoader')}
                />
              </Grid>
              <Grid item xs={3}>
                <ul className='list-inline project-list-cta'>
                  {onCheckPermission('project_management', 'projects', 'EXP') ?
                    <li className='list-inline-item'>
                      <ExportManu
                        disabled={qmeTrackerInfo.list.length === 0}
                        title="Export" files={[{ title: 'Excel', extension: 'xls', key: '' }]}
                        exportReportFn={(file) => exportQmeTracker(file)}
                      />
                    </li> : null
                  }
                </ul>
              </Grid>
            </Grid>
          </Paper>
        }
        {skeletonLoader ? <STableLoader count={9} rowsFill={7} /> :
          <TableListComp
            classNames="qme-tracker-table"
            style={{ maxHeight: (window.innerHeight - 230) + 'px' }}
            heads={qmeTrackerHd}
            sort={sort.sort}
            sort_by={sort.sort_by}
            data={qmeTrackerInfo.list.map((item, index) =>
              <QmeTrackerList
                key={index}
                item={item}
                editQmeTracker={() => setQmeTracker(item)}
              />
            )}
            noRecord={qmeTrackerInfo.list.length ? null : <EmptyCollection title="No records found" />}
            pagination={qmeTrackerInfo.pagination}
            onChangePage={(event, newPage) => getQmeTracker({ ...filter, ...sort, page: newPage + 1, per_page: qmeTrackerInfo.pagination.per_page }, 'pageLoader')}
            onChangeRowsPerPage={(event) => getQmeTracker({ ...filter, ...sort, page: 1, per_page: event.target.value }, 'pageLoader')}
            createSortHandler={createSortHandler}
          />
        }
      </div>
      {qmeTracker ?
        <QmeTrackerCRU
          qmeTracker={qmeTracker}
          users={users}
          toggleModalFn={() => setQmeTracker(null)}
          getResponseBack={() => { setQmeTracker(null); getQmeTracker({ ...filter, ...sort, page: qmeTrackerInfo.pagination.page, per_page: qmeTrackerInfo.pagination.per_page }, 'pageLoader'); }}
        /> : null
      }
      {pageLoader ? <PageLoader /> : null}
    </section>
  )
}
const mapStateToProps = (state) => ({
  lessee: state.sharedReducers.sLesseeList,
})
const mapDispatchToProps = (dispatch) => ({
  getLesseeList: () => dispatch(sLesseeListAc()),
})
export default connect(mapStateToProps, mapDispatchToProps)(QmeTracker);
